import { useRouter } from 'next/navigation';

import omit from 'lodash/omit';
import { URLSearchParams } from 'url';

import type { AddedAddOn, AddedAddOns } from '@/ducks/addOns/types';
import type { TCalculateInvoice } from '@/infra/types/cabin';
import type { AppDispatch } from '@/store';

import { toStringArray } from '@/ducks/filters/utils/primitive';
import { AddOnSellType, type TAddOn, type TSortableAddOn } from '@/helpers/api/graphql/queries/AddonAvailability';
import { getSessionStorageValue } from '@/helpers/util';

import {
  ADD_ONS_SEARCH_PARAM,
  ADDED_ADDONS_SESSION_KEY,
  ADDONS_ACTIONS,
  BAR_TAB_ADD_ON_MASK,
  BAR_TAB_MAX_TOTAL_PRICE,
} from './constants';

// TODO: a lot of functions here... need to distribute em to `./utils/*.ts`

export const serializeData = (addOns?: AddedAddOns) => {
  return Object.keys(addOns || {})
    .sort()
    .reduce<string[]>((codes, code) => {
      if (!addOns?.[code]) {
        return codes;
      }
      return [...codes, addOns[code]! > 1 ? `${code}_${addOns[code]}` : code];
    }, [])
    .join(',');
};

export const deserializeData = (searchParams: URLSearchParams, addOns?: TAddOn[], codes?: string[]) => {
  return toStringArray(searchParams.get(ADD_ONS_SEARCH_PARAM)).reduce<AddedAddOns>((map, value) => {
    const index = value.lastIndexOf('_');
    let code = value;
    let amount = 1;
    if (index !== -1) {
      amount = parseInt(value.substring(index + 1));
      if (isNaN(amount)) {
        amount = 1;
      }

      code = value.substring(0, index);
    }

    const addOn = addOns?.find((addOn) => addOn.code === code);

    if ((!addOns || addOn) && (!codes || codes?.includes(code)) && amount > 0) {
      amount = (map[code] || 0) + amount;
      if (addOn && typeof addOn.maximumQuantity === 'number' && amount > addOn.maximumQuantity) {
        amount = addOn.maximumQuantity;
      }

      return { ...map, [code]: amount };
    }

    return map;
  }, {});
};

export const isAdded = (addOns?: AddedAddOns, addOn?: TAddOn, props: { quantity?: number } = {}) => {
  if (!addOn || !addOns) {
    return false;
  }

  if (typeof props.quantity === 'undefined') {
    return !!addOns![addOn!.code];
  }

  return props.quantity === addOns![addOn!.code];
};

export const isConfigurableAddOn = (addOn: TAddOn) => addOn.nestedAddOns && addOn.nestedAddOns.length > 0;

export const saveInSearchParams = (router: ReturnType<typeof useRouter>, value?: string) => {
  const url = new URL(window.location.toString());
  const { searchParams } = url;

  if (searchParams.has(ADD_ONS_SEARCH_PARAM)) {
    if (value === searchParams.get(ADD_ONS_SEARCH_PARAM)) {
      return;
    }

    searchParams.delete(ADD_ONS_SEARCH_PARAM);
  }

  if (value) {
    searchParams.append(ADD_ONS_SEARCH_PARAM, value);
  }

  router.replace(url.toString(), { scroll: false });
};

export const runAddOnsActions = (dispatch: AppDispatch, values: AddedAddOns) => {
  Object.keys(ADDONS_ACTIONS).forEach((code) => {
    if (typeof values[code] !== 'undefined') {
      dispatch(ADDONS_ACTIONS[code](values[code]! > 0));
    }
  });
};

export const getAddOnPrice = (addOn: TAddOn) => {
  const price = addOn.addOnDetails?.price;

  if (price) {
    let amount = price.amount;
    if (addOn.sellType === AddOnSellType.PerCabin) {
      amount = price.totalAmount;
    }

    return {
      ...price,
      amount,
    };
  }
};

export const getAddOnsTotalPrice = (addOns: TAddOn[], values?: AddedAddOns) => {
  return {
    ...addOns[0]?.addOnDetails?.price,
    amount: addOns.reduce((totalAmount, addOn) => {
      const price = getAddOnPrice(addOn);

      if (!isConfigurableAddOn(addOn)) {
        return totalAmount + (price?.amount || 0) * (values?.[addOn.code] || 0);
      }

      return (
        totalAmount +
        addOn.nestedAddOns!.reduce((amount, addOn) => (price?.amount || 0) * (values?.[addOn.code] || 0) + amount, 0)
      );
    }, 0),
  };
};

export const getAddOnCodes = (addOn: TAddOn) => {
  return addOn.nestedAddOns?.map(({ code }) => code) || [addOn.code];
};

export const isEmptyValues = (addedAddOns: AddedAddOns) => {
  const values = new Set(Object.values(addedAddOns));
  return (values.size === 1 && values.has(0)) || values.size === 0;
};

export const getAddOnsCalcInvoicePayload = (addedAddOns: AddedAddOn[], sailorNumber: number) => {
  return addedAddOns.reduce<{ code: string; quantity: number }[]>((result, addOn) => {
    const item = { code: addOn.code, quantity: addOn.quantity };
    if (
      (addOn.sellType === AddOnSellType.PerSailor && sailorNumber === 1) ||
      addOn.sellType === AddOnSellType.PerCabin
    ) {
      return [...result, item];
    }

    return result;
  }, []);
};

export const sortByPriority = <T extends TSortableAddOn>(a: T, b: T) =>
  (a.bookingSortPriority || -1) - (b.bookingSortPriority || -1);

export const checkIfAddOnAddedToSessionStorage = (code: string) => {
  const trulyAddedAddons = getSessionStorageValue(ADDED_ADDONS_SESSION_KEY) as Record<string, number>;
  const quantity = trulyAddedAddons[code];
  return quantity && quantity > 0;
};

export const getAddOnFromCalcInvoiceData = ({
  calculateInvoiceData,
  code,
}: {
  calculateInvoiceData?: TCalculateInvoice;
  code: string;
}) => {
  const details = calculateInvoiceData?.cabinInvoices[0]?.bookingPriceDetails;
  const addOn = details?.addOnPrices.find((addOn) => addOn.code === code);
  return addOn && checkIfAddOnAddedToSessionStorage(addOn.code) ? addOn : undefined;
};

export const getAddOnPriceFromCalcInvoiceData = ({
  calculateInvoiceData,
  payInFullPromoApplied,
}: {
  calculateInvoiceData?: TCalculateInvoice;
  payInFullPromoApplied?: boolean;
}) => {
  const details = payInFullPromoApplied
    ? calculateInvoiceData?.cabinInvoices?.[0]?.fullPayPrice
    : calculateInvoiceData?.cabinInvoices?.[0]?.bookingPriceDetails;

  return (
    details?.addOnPrices?.reduce(
      (total, { amount, code }) => total + (checkIfAddOnAddedToSessionStorage(code) ? amount : 0),
      0,
    ) || 0
  );
};

export const isBarTab = (addOn?: TAddOn) => {
  return (addOn && BAR_TAB_ADD_ON_MASK.test(addOn.code)) || false;
};

const getBarTabsTotalPrice = (addOns?: TAddOn[], addedAddOns?: AddedAddOns) =>
  addOns?.reduce((total, addOn) => {
    if (isAdded(addedAddOns, addOn) && isBarTab(addOn)) {
      return total + (getAddOnsTotalPrice([addOn], addedAddOns)?.amount || 0);
    }
    return total;
  }, 0) || 0;

export const checkIfBarTabAddOnExceedsLimit = (
  addOns?: TAddOn[],
  addedAddOns?: AddedAddOns,
  addOn?: TAddOn,
  { excludeCurrentAddOn, quantity = 1 }: { excludeCurrentAddOn?: boolean; quantity?: number } = {},
) => {
  const _addedAddOns = excludeCurrentAddOn && addOn ? omit(addedAddOns, addOn?.code) : addedAddOns;
  return (
    ((addOn && isBarTab(addOn) && getAddOnPrice(addOn)?.amount) || 0) * quantity +
      getBarTabsTotalPrice(addOns, _addedAddOns) >=
    BAR_TAB_MAX_TOTAL_PRICE
  );
};

export const checkIfBarTabsTotalPriceExceedsLimit = (addOns?: TAddOn[], addedAddOns?: AddedAddOns) => {
  return getBarTabsTotalPrice(addOns, addedAddOns) >= BAR_TAB_MAX_TOTAL_PRICE;
};
